import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, KeyRound } from "lucide-react";
import * as api from "../../lib/api";
import { toast } from "sonner";

export function LoginPage() {
    const [searchParams] = useSearchParams();
    const [ssoEnabled, setSsoEnabled] = useState(null); // null = loading
    const backendUrl = window.location.origin;

    useEffect(() => {
        api.getSsoStatus()
            .then((data) => setSsoEnabled(data.enabled))
            .catch(() => setSsoEnabled(false));
    }, []);

    useEffect(() => {
        const error = searchParams.get("error");
        if (error === "sso_failed") {
            toast.error("La connexion Microsoft a échoué. Veuillez réessayer ou contacter le support IT.");
        }
    }, [searchParams]);

    const handleMicrosoftLogin = () => {
        window.location.href = `${backendUrl}/api/auth/sso/login`;
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="login-page">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <img
                        src="/logo-eggtelsa.webp"
                        alt="EggTelsa"
                        className="h-16 mx-auto mb-4 object-contain"
                        data-testid="login-logo"
                    />
                    <h1 className="text-2xl font-bold text-gray-900">Helpdesk - EggTelsa</h1>
                    <p className="text-sm text-gray-500 mt-1">Portail de support informatique</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    {searchParams.get("error") === "sso_failed" && (
                        <div className="flex items-center gap-2 px-3 py-2.5 mb-4 bg-red-50 border border-red-200 rounded-lg" data-testid="sso-error-banner">
                            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            <p className="text-xs text-red-800">La connexion Microsoft a échoué. Réessayez ou contactez le support.</p>
                        </div>
                    )}

                    {ssoEnabled === null ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        </div>
                    ) : ssoEnabled ? (
                        <>
                            <button
                                onClick={handleMicrosoftLogin}
                                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800 shadow-sm"
                                data-testid="microsoft-login-btn"
                            >
                                <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                                </svg>
                                Se connecter avec Microsoft
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="text-center py-4">
                                <div className="w-12 h-12 mx-auto mb-3 bg-blue-50 rounded-full flex items-center justify-center">
                                    <svg width="24" height="24" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                                        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                                        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                                        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                                    </svg>
                                </div>
                                <p className="text-sm text-gray-600 mb-1">La connexion Microsoft sera bientôt disponible.</p>
                                <p className="text-xs text-gray-400">Contactez votre administrateur pour plus d'informations.</p>
                            </div>

                            <div className="border-t border-gray-200 pt-4 mt-4">
                                <Link
                                    to="/login/local"
                                    className="flex items-center justify-center gap-2 w-full text-xs text-gray-500 hover:text-gray-700 transition-colors py-2"
                                    data-testid="local-login-link"
                                >
                                    <KeyRound className="w-3.5 h-3.5" />
                                    Connexion locale (admin)
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
